import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {lastValueFrom} from 'rxjs';
import {ApiHelperService} from './api-helper.service';
import {SupportAiAnswer, SupportAiConfig, SupportAiConversation, SupportAiHandoff, SupportAiMessage, SupportAiScope} from '../models/support-ai';

interface ApiResponse<T> {success: boolean; data: T; message?: string;}

@Injectable({providedIn: 'root'})
export class SupportAiService {
	private readonly baseUrl = `${this.apiHelper.apiUrl()}/support-ai`;

	constructor(private http: HttpClient, private apiHelper: ApiHelperService) {}

	getConfig(): Promise<SupportAiConfig> {
		return lastValueFrom(this.http.get<ApiResponse<SupportAiConfig>>(`${this.baseUrl}/config`, {headers: this.apiHelper.apiHeaderWithToken()}))
			.then(response => response.data);
	}

	listConversations(): Promise<SupportAiConversation[]> {
		return lastValueFrom(this.http.get<ApiResponse<SupportAiConversation[]>>(`${this.baseUrl}/conversations`, {headers: this.apiHelper.apiHeaderWithToken()}))
			.then(response => response.data);
	}

	createConversation(scope: SupportAiScope): Promise<SupportAiConversation> {
		return lastValueFrom(this.http.post<ApiResponse<SupportAiConversation>>(`${this.baseUrl}/conversations`, {scope}, {headers: this.apiHelper.apiHeaderWithToken()}))
			.then(response => response.data);
	}

	getMessages(conversationId: string): Promise<SupportAiMessage[]> {
		return lastValueFrom(this.http.get<ApiResponse<SupportAiMessage[]>>(`${this.baseUrl}/conversations/${conversationId}/messages`, {headers: this.apiHelper.apiHeaderWithToken()}))
			.then(response => response.data);
	}

	deleteConversation(conversationId: string): Promise<void> {
		return lastValueFrom(this.http.delete(`${this.baseUrl}/conversations/${conversationId}`, {headers: this.apiHelper.apiHeaderWithToken()})).then(() => undefined);
	}

	feedback(conversationId: string, messageId: string, rating: 'up' | 'down'): Promise<void> {
		return lastValueFrom(this.http.post(`${this.baseUrl}/conversations/${conversationId}/feedback`, {messageId, rating}, {headers: this.apiHelper.apiHeaderWithToken()})).then(() => undefined);
	}

	handoffSummary(conversationId: string): Promise<SupportAiHandoff> {
		return lastValueFrom(this.http.post<ApiResponse<SupportAiHandoff>>(`${this.baseUrl}/conversations/${conversationId}/handoff-summary`, {}, {headers: this.apiHelper.apiHeaderWithToken()}))
			.then(response => response.data);
	}

	async sendMessage(
		conversationId: string,
		message: string,
		clientRequestId: string,
		onStatus: (status: string) => void,
		signal: AbortSignal
	): Promise<SupportAiAnswer> {
		const response = await fetch(`${this.baseUrl}/conversations/${conversationId}/messages`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': this.apiHelper.apiHeaderWithToken().get('Authorization') || ''
			},
			body: JSON.stringify({message, clientRequestId}),
			signal
		});
		if (!response.ok || !response.body) throw new Error('Support AI is temporarily unavailable');

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';
		while (true) {
			const result = await reader.read();
			buffer += decoder.decode(result.value || new Uint8Array(), {stream: !result.done});
			const frames = buffer.split('\n\n');
			buffer = frames.pop() || '';
			for (const frame of frames) {
				const event = frame.match(/^event:\s*(.+)$/m)?.[1];
				const dataText = frame.match(/^data:\s*(.+)$/m)?.[1];
				if (!event || !dataText) continue;
				const data = JSON.parse(dataText);
				if (event === 'status') onStatus(data.message);
				if (event === 'error') throw new Error(data.message);
				if (event === 'complete') return data as SupportAiAnswer;
			}
			if (result.done) break;
		}
		throw new Error('Support AI response ended unexpectedly');
	}
}
